import React, { useMemo, useState } from 'react';
import type { WizardState, MedicationRecord, DrugRecord, DripRecord, MedicationKind } from '../types';
import type { Action } from '../state/configReducer';
import MedicationEditor from '../components/MedicationEditor';
import MedicationTypeHelp from '../components/MedicationTypeHelp';
import { getMedicationById } from '../state/configReducer';
import DeleteConfirmationDialog from '../../../components/dialogs/DeleteConfirmationDialog';

interface Props {
  state: WizardState;
  dispatch: React.Dispatch<Action>;
  hospital: string;
}

function generateUniqueMedicationId(prefix: string, existing: Set<string>): string {
  const base = `${prefix}-${Date.now()}`;
  if (!existing.has(base)) return base;
  let i = 2;
  while (existing.has(`${base}-${i}`)) i++;
  return `${base}-${i}`;
}

const blankDrug = (id: string): DrugRecord => ({
  id,
  slug: id,
  kind: 'drug',
  name: '',
  howToGive: 'IV',
  comment: null,
  warnText: null,
  administrationInstructions: null,
  type: null,
  dose_per_kg: null,
  dose_unit: 'mg',
  dose_range: null,
  concentration: null,
  maxDose: null,
  minDose: null,
  minDoseUnit: null,
  constant_dose: null,
  shouldDispalyConcentration: null,
  cases: null
});

const blankDrip = (id: string): DripRecord => ({
  id,
  slug: id,
  kind: 'drip',
  name: '',
  howToGive: 'IV',
  comment: null,
  warnText: null,
  administrationInstructions: null,
  calc_type: 'DilutionInstructions',
  dose_unit: 'mcg',
  allowed_dose_range: null,
  dose_per_kg_per_min: null,
  dose_per_kg_per_hour: null,
  default_dilution_volume_ml: 50,
  default_dilution_volume_unit: 'ml',
  target_volume_ml_per_hour: 1,
  existing_dilution_concentration: null,
  existing_dilution_concentration_dose_unit: null,
  max_dose_per_hour: null,
  cases: null
});

const StepMedications: React.FC<Props> = ({ state, dispatch, hospital }) => {
  const { medications } = state.data;
  const search = state.ui.medicationSearch;
  const selectedId = state.ui.selectedMedicationId;
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return medications;
    return medications.filter(m => {
      const protocolNames = state.data.assignments
        .filter(a => a.medicationId === m.id)
        .map(a => state.data.protocols.find(p => p.id === a.protocolId)?.name || '')
        .join(' ');
      return [
        m.name,
        m.id,
        m.slug || '',
        m.howToGive || '',
        m.kind,
        protocolNames
      ].some(field => field.toLowerCase().includes(term));
    });
  }, [medications, search, state.data.assignments, state.data.protocols]);

  const onAddMedication = (kind: MedicationKind) => {
    const existing = new Set(medications.map(m => m.id));
    const id = generateUniqueMedicationId(kind === 'drug' ? 'new-drug' : 'new-drip', existing);
    const med: MedicationRecord = kind === 'drug' ? blankDrug(id) : blankDrip(id);
    dispatch({ type: 'ADD_MEDICATION', medication: med });
    dispatch({ type: 'SELECT_MEDICATION', id });
  };

  const onDeleteMedication = () => {
    if (!selectedId) return;
    if (!getMedicationById(state.data, selectedId)) return;
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteMedication = () => {
    if (!selectedId) return;
    dispatch({ type: 'DELETE_MEDICATION', id: selectedId });
    setDeleteConfirmOpen(false);
  };

  const selected = selectedId ? getMedicationById(state.data, selectedId) : undefined;

  return (
    <div className="resus-admin__master-detail">
      <aside className="resus-admin__list-pane" aria-label="רשימת תרופות">
        <div className="resus-admin__list-header">
          <input
            className="resus-admin__search"
            type="search"
            placeholder="חיפוש תרופה..."
            value={search}
            onChange={(e) => dispatch({ type: 'SET_MEDICATION_SEARCH', value: e.target.value })}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" className="resus-admin__btn resus-admin__btn--accent" onClick={() => onAddMedication('drug')}>
              + תרופה
            </button>
            <button type="button" className="resus-admin__btn resus-admin__btn--accent" onClick={() => onAddMedication('drip')}>
              + דריפ
            </button>
          </div>
        </div>

        <ul className="resus-admin__list">
          {filtered.length === 0 && (
            <li className="resus-admin__subtle">אין תרופות תואמות לחיפוש.</li>
          )}
          {filtered.map(m => (
            <li key={m.id}>
              <button
                type="button"
                className={`resus-admin__list-row${m.id === selectedId ? ' resus-admin__list-row--selected' : ''}`}
                onClick={() => dispatch({ type: 'SELECT_MEDICATION', id: m.id })}
                style={{ width: '100%', border: 'none' }}
              >
                <div className="resus-admin__list-row__main">
                  <span className="resus-admin__list-row__name">
                    {m.name || <em className="resus-admin__subtle">(ללא שם)</em>}
                  </span>
                  <span className="resus-admin__list-row__sub">
                    {m.kind === 'drug' ? 'תרופה' : 'דריפ'}
                    {m.howToGive ? ` · ${m.howToGive}` : ''}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <div className="resus-admin__detail-pane">
        <MedicationTypeHelp />
        {!selected && (
          <p className="resus-admin__subtle">בחרו תרופה או הוסיפו חדשה.</p>
        )}
        {selected && (
          <MedicationEditor
            key={selected.id}
            medication={selected}
            state={state}
            hospital={hospital}
            dispatch={dispatch}
            onDelete={onDeleteMedication}
          />
        )}
      </div>

      <DeleteConfirmationDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={confirmDeleteMedication}
        title={`למחוק את "${selected?.name || selected?.id || ''}"? פעולה זו תסיר גם את כל ההקצאות לפרוטוקולים.`}
        showError={false}
        errorMessage=""
        onCloseError={() => {}}
      />
    </div>
  );
};

export default StepMedications;
